import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";

import { format } from "date-fns";

import { Typography } from "@mui/material";

import { faSliders } from "@fortawesome/pro-regular-svg-icons";

import { Icon, Modal } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { useReconnectEmailMutation } from "@/pages/Private/redux/email/email.api";
import { classNames } from "@/utils/classNames";

import { EmailPanel } from "./EmailPanel";
import { PendingSchedulesDialog } from "./PendingSchedulesDialog";
import { SettingsSubBlock } from "./SettingsSection";
import { useSenderDeletion } from "../hooks/useSenderDeletion";
import { Email, isEmailLoggedOut } from "../schema/email";

/**
 * A single connected email account (primary or secondary) inside the Email card.
 * Each row owns its own settings panel + disconnect flow so several accounts can
 * be managed independently on the Connected Accounts tab.
 */
export const EmailAccountRow: FunctionComponent<{ email: Email }> = ({ email }) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`account.connectedAccounts.${key}`);

	const [showEmailSettingsModal, setShowEmailSettingsModal] = useState<boolean>(false);
	const [showDisconnectModal, setShowDisconnectModal] = useState<boolean>(false);

	const [reconnectEmail, { isLoading: isReconnecting }] = useReconnectEmailMutation();

	// Connection state comes from the email's `status` field — the same source the user-overview
	// email table uses (isEmailLoggedOut) — so a logged-out mailbox surfaces a Reconnect action here.
	const loggedOut = isEmailLoggedOut(email.status);

	const handleReconnect = async () => {
		try {
			const response = await reconnectEmail({
				id: email.id,
				successRedirectUrl: window.location.href || undefined,
			}).unwrap();

			if (response.url) {
				// Full-page navigation (not window.open) so a popup blocker can't suppress it.
				window.location.href = response.url;
			}
		} catch (error) {
			console.error(error);
		}
	};

	const {
		initiateDeletion,
		confirmDeletion,
		cancelDeletion,
		showPendingDialog,
		totalCount,
		campaigns,
		isCheckingPending,
		isDeleting,
	} = useSenderDeletion({
		senderType: "email",
		emailId: email.id,
		onSuccess: () => setShowDisconnectModal(false),
	});

	const isPrimary = email.type === "primary";

	return (
		<SettingsSubBlock>
			<div className="flex flex-wrap items-center gap-x-4 gap-y-3">
				<div className="min-w-0 flex-1">
					<div className="flex items-center gap-2.5">
						<span
							className={classNames(
								"flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md text-xs font-bold text-white",
								isPrimary ? "bg-status-error" : "bg-blue-action"
							)}
						>
							@
						</span>
						<span className="truncate text-sm text-text">
							{email.email}
							{email.provider && <span className="text-text-meta"> · {email.provider}</span>}
						</span>
						<span className="flex-shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-text-meta">
							{isPrimary ? ts("primaryLabel") : ts("secondaryLabel")}
						</span>
						<span
							className={classNames(
								"flex-shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold",
								loggedOut
									? "bg-status-error-light text-status-error"
									: "bg-green-200 text-green-700"
							)}
						>
							{loggedOut ? ts("loggedOut") : ts("connected")}
						</span>
					</div>
					<div
						className={classNames(
							"mt-1.5 text-[12.5px]",
							loggedOut ? "text-status-error" : "text-text-meta"
						)}
					>
						{email.createdAt &&
							`${ts("emailConnectedOn").replace(
								"{{date}}",
								format(new Date(email.createdAt), "d MMM yyyy")
							)} · `}
						{loggedOut ? ts("emailLoggedOutMeta") : ts("emailActiveMeta")}
					</div>
				</div>

				<div className="flex flex-shrink-0 items-center gap-3">
					{loggedOut && (
						<Button
							color={ButtonColor.ACTION}
							isLoading={isReconnecting}
							size={ButtonSize.S}
							title={ts("reconnect")}
							type="button"
							onClick={handleReconnect}
						/>
					)}
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						image={<Icon className="mr-2 h-3.5 w-3.5" icon={faSliders} />}
						size={ButtonSize.S}
						title={ts("editEmailSettings")}
						type="button"
						onClick={() => setShowEmailSettingsModal(true)}
					/>
					<Button
						color={ButtonColor.DANGER_SECONDARY}
						size={ButtonSize.S}
						title={ts("disconnect")}
						type="button"
						onClick={() => setShowDisconnectModal(true)}
					/>
				</div>
			</div>

			<Modal
				handleClose={() => setShowDisconnectModal(false)}
				handleSave={() => {
					initiateDeletion();
				}}
				isLoading={isDeleting || isCheckingPending}
				isOpened={showDisconnectModal}
				title="Disconnect E-Mail"
			>
				<div>
					<Typography variant="h6">
						Are you sure you want to disconnect this E-Mail address? This will remove the E-Mail
						address from our system. You will need to connect it again to continue to send and
						receive emails.
					</Typography>
				</div>
			</Modal>

			<PendingSchedulesDialog
				campaigns={campaigns}
				isLoading={isDeleting}
				isOpen={showPendingDialog}
				onClose={cancelDeletion}
				onConfirm={confirmDeletion}
				senderType="email"
				totalCount={totalCount}
			/>

			{showEmailSettingsModal && (
				<EmailPanel email={email} handleClose={() => setShowEmailSettingsModal(false)} />
			)}
		</SettingsSubBlock>
	);
};
